import { useForm } from 'react-hook-form'
import { IKoreanLanguage, KoreanLanguageType } from '../../../Entities/koreanLanguage'
import { useKoreanLanguage } from 'Hooks'
import { useNavigate } from 'react-router-dom'
import { KoreanLanguagePath } from 'Constants/Navigation'
import { useKoreanLanguageConfig } from './useKoreanLanguageConfig'
import { yupResolver } from '@hookform/resolvers/yup'
import { APP } from 'Constants/App'
import * as yup from 'yup'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import httpClient from 'Service'

export type SchoolItem = {
  schoolId: number
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
  login?: string | null
  password?: string | null
}

export type SchoolCredentials = {
  schoolId?: number
  schoolNameUz?: string | null
  schoolNameRu?: string | null
  schoolNameKo?: string | null
  login?: string | null
  password?: string | null
  credentialsConfigured?: boolean
}

export type RegionItem = {
  regionCode: number
  regionName: string
}

export type RegionSchoolDetail = {
  regionCode: number
  regionName: string
  schoolCount: number
  schools: SchoolItem[]
}

export type CreateSchoolPayload = {
  regionCode: number
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
  login?: string | null
  password?: string | null
}

export type UpdateSchoolPayload = {
  regionCode: number
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
  login?: string | null
  password?: string | null
}

const getRegionsList = async (): Promise<RegionItem[]> => {
  const response = await httpClient.get('/api/org/schools/regions')
  return response.data
}

const getRegionSchoolDetail = async (regionCode: number): Promise<RegionSchoolDetail> => {
  const response = await httpClient.get(`/api/org/schools/region/${regionCode}`)
  return response.data
}

const getSchoolCredentials = async (schoolId: number): Promise<SchoolCredentials> => {
  const response = await httpClient.get(`/api/admin/schools/${schoolId}/credentials`)
  return response.data
}

const addNewSchool = async (payload: CreateSchoolPayload) => {
  const response = await httpClient.post('/api/account/schools', payload)
  return response.data
}

const updateSchool = async ({ schoolId, payload }: { schoolId: number; payload: UpdateSchoolPayload }) => {
  const response = await httpClient.patch(`/api/account/schools/${schoolId}`, payload)
  return response.data
}

const deleteSchool = async (schoolId: number) => {
  const response = await httpClient.delete(`/api/account/schools/${schoolId}`)
  return response.data
}

export const useKoreanLanguage2 = () => {
  const queryClient = useQueryClient()

  const regionsQuery = useQuery({
    queryKey: ['regions-list'],
    queryFn: getRegionsList,
  })

  const { mutate: addNewSchoolMutation } = useMutation({
    mutationFn: addNewSchool,
    mutationKey: ['new-school'],
    onSuccess: () => {
      regionsQuery.refetch()
      toast.success('New school successfully added !')
    },
  })
  const updateSchoolMutation = useMutation({
    mutationFn: updateSchool,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regions-list'] })
      queryClient.invalidateQueries({ queryKey: ['region-school-detail'] })
      toast.success('학교가 성공적으로 수정되었습니다.')
    },
  })

  const deleteSchoolMutation = useMutation({
    mutationFn: deleteSchool,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regions-list'] })
      queryClient.invalidateQueries({ queryKey: ['region-school-detail'] })
      toast.success('학교가 성공적으로 삭제되었습니다.')
    },
  })

  return {
    regions: regionsQuery.data ?? [],
    isRegionsLoading: regionsQuery.isLoading,
    isRegionsFetching: regionsQuery.isFetching,
    addNewSchoolMutation,
    updateSchoolMutation,
    deleteSchoolMutation,
  }
}

export const useKoreanSchoolDetail = (regionCode?: number | null) => {
  return useQuery({
    queryKey: ['region-school-detail', regionCode],
    queryFn: () => getRegionSchoolDetail(regionCode as number),
    enabled: !!regionCode,
  })
}

export const useKoreanSchoolCredentials = (schoolId?: number | null) => {
  return useQuery({
    queryKey: ['school-credentials', schoolId],
    queryFn: () => getSchoolCredentials(schoolId as number),
    enabled: !!schoolId,
  })
}

export const useKoreanLanguageForm = (type: KoreanLanguageType, id?: string) => {
  const navigate = useNavigate()
  const { subPath } = useKoreanLanguageConfig()
  const { create, update } = useKoreanLanguage({ initList: false, type })

  const schema = yup.object().shape({
    titleKr: yup.string().required(APP.REQUIRED_FIELD),
    titleRu: yup.string().required(APP.REQUIRED_FIELD),
    titleUz: yup.string().required(APP.REQUIRED_FIELD),
    contentKr: yup.string().required(APP.REQUIRED_FIELD),
    contentRu: yup.string().required(APP.REQUIRED_FIELD),
    contentUz: yup.string().required(APP.REQUIRED_FIELD),
  })

  const form = useForm<IKoreanLanguage>({
    defaultValues: {
      titleRu: '',
      titleUz: '',
      titleKr: '',
      contentRu: '',
      contentUz: '',
      contentKr: '',
    },
    resolver: yupResolver(schema),
  })

  const onSubmit = (data: IKoreanLanguage) => {
    if (id) {
      update.mutate({ id, data, action: () => navigate(`/${KoreanLanguagePath.main}/${subPath}`) })
    } else {
      create.mutate({ data, action: () => navigate(`/${KoreanLanguagePath.main}/${subPath}`) })
    }
  }

  return {
    form,
    onSubmit,
    isLoading: create.isLoading || update.isLoading,
  }
}
